import { PermissionType } from '@/enums/PermissionType';
import type {
  IDayCloseDetailResponse,
  IDaySummaryRow,
} from '@/interfaces/IDayClose';
import { roundPrice } from '@/utils';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
} from '../tool-types';
import {
  getOptionalBoolean,
  getOptionalNumber,
  getOptionalStringArray,
  normalizeBusinessDateInput,
} from '../tool-args';
import { axiosGetData, setIfDefined, statsUrl } from '../tool-http';
import { ensureAllCajasOrThrow, ensurePermissionOrThrow } from '../tool-scope';
import { omitNullish } from '../sanitize';
import {
  addDaysToDateStr,
  computeLocalBusinessDay,
} from '../../gpthelp-business-day';

const ensureDayCloseAccess = (deps: ICartadigitalAdminHostToolDeps) => {
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.StatisticsView,
    message: 'No tiene permisos para ver estadísticas',
  });
  ensureAllCajasOrThrow(deps);
};

const DAY_SUMMARY_MAX_DAYS = 92;

const daysBetween = (from: string, to: string) =>
  Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() -
      new Date(`${from}T00:00:00Z`).getTime()) /
      86_400_000,
  );

/**
 * Period sums of the sales fields and collectedTotal ('Cobrado'); deliveryFees
 * and collectedTotal only when known for every day. Null without rows or when
 * the days are not in one currency.
 */
const sumDaySummaryRows = (rows: IDaySummaryRow[], deliveryFeesKnown: boolean) => {
  const [first] = rows;
  if (
    !first ||
    rows.some((row) => row.mixedCurrencies || row.currency !== first.currency)
  ) {
    return null;
  }
  const sum = (pick: (row: IDaySummaryRow) => number) =>
    roundPrice(rows.reduce((total, row) => total + pick(row), 0));
  const collectedTotalKnown = rows.every((row) => row.collectedTotal != null);
  return omitNullish({
    orders: rows.reduce((total, row) => total + row.orders, 0),
    grossSales: sum((row) => row.grossSales),
    deliveryFees: deliveryFeesKnown ? sum((row) => row.deliveryFees ?? 0) : null,
    benefitsTotal: sum((row) => row.benefitsTotal),
    netSales: sum((row) => row.netSales),
    dayTotal: sum((row) => row.dayTotal),
    collectedTotal: collectedTotalKnown
      ? sum((row) => row.collectedTotal ?? 0)
      : null,
  });
};

const DAYCLOSE_SECTIONS = ['categories', 'topProducts', 'hourly'] as const;
type TDaycloseSection = (typeof DAYCLOSE_SECTIONS)[number];

export const daycloseTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'dayclose_summary',
      description:
        "One row per business day with money activity: net sales, collected, over/short, orders, covers, avg ticket, tenders split, tips, non-sale collections (top-ups). Use it for money collected (top-ups included), day closes (over/short, tenders, tips) and for the netSales and delivery fees of cost or profit answers. For 'how much did I sell' on a day or period (not money collected, cost or profit), call stats_orders instead, with its display argument set to true: it shows the sales card. live:true rows are computed on the fly (no stored close yet); stale:true stored closes are outdated by later edits. totals holds the period sums.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          from: {
            type: 'string',
            description:
              'Start BUSINESS date (yyyy-MM-dd). Defaults to 30 days before today.',
          },
          to: {
            type: 'string',
            description:
              'End BUSINESS date (yyyy-MM-dd), inclusive. Defaults to the current business date.',
          },
        },
      },
    },
    policy: 'read',
    isAvailable: (gate) =>
      gate.hasRestaurantScope && gate.canViewStats && gate.allCajas,
    async execute(args, { deps, scope }) {
      ensureDayCloseAccess(deps);

      const { currentBusinessDate } = computeLocalBusinessDay({
        timeZone: scope.timeZone,
        startHour: deps.restaurantStore.selectedBranch?.businessDayStartHour ?? 0,
      });

      const to =
        normalizeBusinessDateInput({ obj: args, key: 'to', timeZone: scope.timeZone }) ??
        currentBusinessDate;
      const from =
        normalizeBusinessDateInput({ obj: args, key: 'from', timeZone: scope.timeZone }) ??
        addDaysToDateStr(to, -30);

      const spanDays = daysBetween(from, to);
      if (spanDays < 0) {
        throw new HostToolError('INVALID_ARGS', '"from" must not be after "to"', {
          from,
          to,
        });
      }
      if (spanDays >= DAY_SUMMARY_MAX_DAYS) {
        throw new HostToolError(
          'INVALID_ARGS',
          `Range too large (max ${DAY_SUMMARY_MAX_DAYS} days)`,
          { from, to },
        );
      }

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      qs.set('from', from);
      qs.set('to', to);

      const data = await axiosGetData<{ rows: IDaySummaryRow[] }>(
        `${statsUrl('/day-summary')}?${qs.toString()}`,
      );

      const restaurantCurrency = deps.restaurantStore.restaurant?.currency ?? null;
      const apiRows = data.rows ?? [];

      const rows = apiRows.map((row) => {
        const { windowFrom, windowTo, currency, mixedCurrencies, amended, ...rest } =
          row;
        return omitNullish({
          ...rest,
          currency:
            currency && currency !== restaurantCurrency ? currency : null,
          mixedCurrencies: mixedCurrencies || null,
          amended: amended || null,
        });
      });
      // omitNullish hides a missing deliveryFees; name the days whose fees are
      // unknown so a sum over the rows is not taken as complete.
      const datesWithoutDeliveryFees = apiRows
        .filter((row) => row.deliveryFees == null && row.orders > 0)
        .map((row) => row.businessDate);
      const totals = sumDaySummaryRows(
        apiRows,
        datesWithoutDeliveryFees.length === 0,
      );

      return {
        from,
        to,
        ...(totals ? { totals } : {}),
        ...(datesWithoutDeliveryFees.length > 0 ? { datesWithoutDeliveryFees } : {}),
        rows,
      };
    },
  },
  {
    definition: {
      name: 'dayclose_get',
      description:
        "Full 'Cierre del día' report for ONE business date: KPIs, money waterfall, tenders, tips, collections, non-sale collections, adjustments, club section, cash sessions, comparisons and channels. Request the unbounded sections (categories/topProducts/hourly) only when needed via `sections`. seq is the Z number; the agent cannot regenerate stale closes - suggest doing it in the UI.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          date: {
            type: 'string',
            description: 'Business date (yyyy-MM-dd). Required.',
          },
          live: {
            type: 'boolean',
            default: false,
            description:
              'Compute a live preview instead of reading the stored snapshot.',
          },
          version: {
            type: 'number',
            description: 'Specific stored version (defaults to the latest).',
          },
          sections: {
            type: 'array',
            items: { type: 'string', enum: [...DAYCLOSE_SECTIONS] },
            description:
              'Extra unbounded sections to include: categories, topProducts, hourly.',
          },
        },
        required: ['date'],
      },
    },
    policy: 'read',
    isAvailable: (gate) =>
      gate.hasRestaurantScope && gate.canViewStats && gate.allCajas,
    async execute(args, { deps, scope }) {
      ensureDayCloseAccess(deps);

      const date = normalizeBusinessDateInput({
        obj: args,
        key: 'date',
        timeZone: scope.timeZone,
      });
      if (!date) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "date"', {
          date: args.date,
        });
      }

      const live = getOptionalBoolean(args, 'live') ?? false;
      const versionRaw = getOptionalNumber(args, 'version');
      const version =
        versionRaw !== null && Number.isFinite(versionRaw) && versionRaw >= 1
          ? Math.floor(versionRaw)
          : null;

      const sectionsRaw = getOptionalStringArray(args, 'sections') ?? [];
      const invalidSections = sectionsRaw.filter(
        (s) => !DAYCLOSE_SECTIONS.includes(s as TDaycloseSection),
      );
      if (invalidSections.length) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "sections"', {
          invalid: invalidSections,
          allowed: DAYCLOSE_SECTIONS,
        });
      }
      const sections = new Set(sectionsRaw as TDaycloseSection[]);

      const qs = new URLSearchParams();
      qs.set('r', String(scope.restaurantId));
      qs.set('b', String(scope.branchId));
      if (live) qs.set('live', '1');
      setIfDefined(qs, 'version', version);

      const data = await axiosGetData<IDayCloseDetailResponse>(
        `${statsUrl(`/day-closes/${date}`)}?${qs.toString()}`,
      );

      const p = data.payload;

      // schemaVersion-1 snapshots lack comparisons/channels/topProducts/hourly
      // — omitNullish drops the absent ones.
      return omitNullish({
        live: data.live,
        stale: data.stale,
        versions: data.versions,
        header: p.header,
        kpis: p.kpis,
        waterfall: p.waterfall,
        tenders: p.tenders,
        tipsByBucket: p.tipsByBucket,
        collections: p.collections,
        nonSaleCollections: p.nonSaleCollections,
        adjustments: p.adjustments,
        club: p.club,
        cashSessions: p.cashSessions,
        comparisons: p.comparisons ?? null,
        channels: p.channels ?? null,
        categories: sections.has('categories') ? p.categories ?? null : null,
        topProducts: sections.has('topProducts') ? p.topProducts ?? null : null,
        hourly: sections.has('hourly') ? p.hourly ?? null : null,
      });
    },
  },
];
